import ChevronLeft from '@mui/icons-material/ChevronLeft';
import ChevronRight from '@mui/icons-material/ChevronRight';
import MoreHoriz from '@mui/icons-material/MoreHoriz';
import { ComponentPropsWithoutRef, FC } from 'react';

/**
 * 1) Pagination – container <nav> element for accessibility.
 */
export const Pagination: FC<ComponentPropsWithoutRef<'nav'>> = props => (
  <nav role="navigation" aria-label="pagination" {...props} />
);

/**
 * 2) PaginationContent – a <ul> that holds all pagination items.
 */
export const PaginationContent: FC<ComponentPropsWithoutRef<'ul'>> = props => <ul {...props} />;

/**
 * 3) PaginationItem – a <li> wrapper for each pagination element.
 */
export const PaginationItem: FC<ComponentPropsWithoutRef<'li'>> = props => <li {...props} />;

/**
 * 4) PaginationLink – a clickable link (HTML <button>) for pagination.
 *    isActive can help set aria-current or other logic.
 */
export interface PaginationLinkProps extends Omit<ComponentPropsWithoutRef<'button'>, 'type'> {
  isActive?: boolean;
}

export const PaginationLink: FC<PaginationLinkProps> = ({ isActive, children, ...props }) => (
  <button type="button" aria-current={isActive ? 'page' : undefined} {...props}>
    {children}
  </button>
);

/**
 * 5) PaginationPrevious – "Previous" button (left icon).
 *    We directly include <ChevronLeft /> here, but no Joy styling.
 */
export const PaginationPrevious: FC<PaginationLinkProps> = ({ children, ...props }) => (
  <PaginationLink aria-label="Go to previous page" {...props}>
    <ChevronLeft />
    {/* If you want text next to the icon, pass as children */}
    {children}
  </PaginationLink>
);

/**
 * 6) PaginationNext – "Next" button (right icon).
 */
export const PaginationNext: FC<PaginationLinkProps> = ({ children, ...props }) => (
  <PaginationLink aria-label="Go to next page" {...props}>
    {children}
    <ChevronRight />
  </PaginationLink>
);

/**
 * 7) PaginationEllipsis – "..." indicator.
 */
export const PaginationEllipsis: FC<ComponentPropsWithoutRef<'span'>> = ({ children, ...props }) => (
  <span aria-label="More pages" {...props}>
    <MoreHoriz />
    {children}
  </span>
);
